import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { ProgressBarBar } from "@faire/slate/private/progress-bar/ProgressBarBar";
import { ProgressBarBase } from "@faire/slate/private/progress-bar/ProgressBarBase";
import { ProgressBarIcon } from "@faire/slate/private/progress-bar/ProgressBarIcon";
import {
  IProgressBarPropsConstraints,
  IProgressBarWebProps,
  ProgressBarVariant,
} from "@faire/slate/private/progress-bar/types";
import React from "react";

type IProps = IProgressBarWebProps & {
  /**
   * The current progress value (0-1)
   */
  progress: number;
  /**
   * The variant of the progress bar
   * @default "success"
   */
  variant?: ProgressBarVariant;
  /**
   * Whether to display the trailing checkmark icon when progress=1. Only available for variant="success".
   * @default false
   */
  showIcon?: boolean;

  // DO NOT ADD any new prop without aligning with Slate team first
};

/**
 * A progress bar is used to indicate progress of a task, such as a retailer’s progress on reaching a brand minimum
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Progress-Bar-2242efb5c25a80a78ecadbdb725c666a | Slate/ProgressBar}
 */
export const ProgressBar: React.FC<IProgressBarPropsConstraints<IProps>> = ({
  variant = "success",
  progress,
  showIcon = false,
  ...props
}) => (
  <ProgressBarBase
    className={progressBarStyle.cls({
      variant,
      className: componentCls(),
    })}
    data-component-name="Slate/ProgressBar"
  >
    <ProgressBarBar {...props} value={progress * 100} />
    {variant === "success" ? (
      <ProgressBarIcon isVisible={progress === 1 && showIcon} />
    ) : null}
  </ProgressBarBase>
);

const progressBarStyle = tsv({
  variants: {
    variant: {
      success: "fs-progress-bar--success",
      neutral: "fs-progress-bar--neutral",
    },
  },
  base: "fs-progress-bar",
});
